import * as lockIcon from '@nordhealth/icons/lib/assets/navigation-toggle-lock.js'
import * as unlockIcon from '@nordhealth/icons/lib/assets/navigation-toggle.js'
import { html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ref } from 'lit/directives/ref.js'

import { DirectionController } from '../common/controllers/DirectionController.js'
import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import componentStyle from '../common/styles/Component.css'

import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './NavToggle.css'

import '../button/Button.js'
import '../visually-hidden/VisuallyHidden.js'

Icon.registerIcon(unlockIcon)
Icon.registerIcon(lockIcon)

/**
 * Nav toggle hides and shows the primary navigation inside `<nord-layout>`.
 *
 * @status ready
 * @category navigation
 *
 * @localization label - Accessible label for the nav toggle button.
 *
 * @parent Navigation
 */
@customElement('nord-nav-toggle')
export default class NavToggle extends FocusableMixin(LitElement) {
  static styles = [componentStyle, style]

  private direction = new DirectionController(this)
  private localization = new LocalizeController<'nord-nav-toggle'>(this)
  private layoutObserver?: MutationObserver
  private observedLayout?: HTMLElement

  /**
   * Icon shown in the resting state. Accepts any registered icon name.
   * Defaults to `navigation-toggle`.
   */
  @property({ reflect: true }) icon: string = unlockIcon.title

  /**
   * Icon shown on hover/focus (≥ 768px), swapping in for `icon`.
   * Defaults to `navigation-toggle-lock`.
   */
  @property({ reflect: true, attribute: 'icon-active' }) iconActive: string = lockIcon.title

  /**
   * When set, the toggle finds its ancestor `<nord-layout>` and hides
   * itself on wide viewports while the layout's nav is open. Useful when
   * placing the toggle outside the `nav-toggle` slot.
   */
  @property({ type: Boolean, reflect: true, attribute: 'auto-hide' }) autoHide: boolean = false

  /**
   * Forwards the `square` attribute to the underlying `<nord-button>`,
   * giving the toggle a fixed square footprint instead of the default
   * pill shape.
   */
  @property({ type: Boolean, reflect: true }) square: boolean = false

  /**
   * Size of the underlying `<nord-button>`. Defaults to `m`.
   */
  @property({ reflect: true }) size: 's' | 'm' = 'm'

  /**
   * Size of the inner `<nord-icon>` elements. Accepts the same values
   * as `<nord-icon>`'s `size`. Defaults to `m`.
   */
  @property({ reflect: true, attribute: 'icon-size' }) iconSize: 'xxs' | 'xs' | 's' | 'm' | 'l' = 'm'

  /**
   * Reflects whether `auto-hide` is engaged (i.e. the ancestor layout
   * currently has `nav-open`). The CSS hides the host based on this.
   */
  @state() private autoHidden: boolean = false

  connectedCallback() {
    super.connectedCallback()
    this.startAutoHide()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.stopAutoHide()
  }

  protected updated(changed: Map<string, unknown>) {
    super.updated?.(changed)
    if (changed.has('autoHide')) {
      this.stopAutoHide()
      this.startAutoHide()
    }
    if (changed.has('autoHidden')) {
      this.toggleAttribute('data-auto-hidden', this.autoHidden)
    }
  }

  private startAutoHide() {
    if (!this.autoHide) {
      return
    }
    const layout = this.closest<HTMLElement>('nord-layout')
    if (!layout) {
      return
    }
    this.observedLayout = layout
    this.autoHidden = layout.hasAttribute('nav-open')
    this.layoutObserver = new MutationObserver(() => {
      this.autoHidden = layout.hasAttribute('nav-open')
    })
    this.layoutObserver.observe(layout, {
      attributes: true,
      attributeFilter: ['nav-open'],
    })
  }

  private stopAutoHide() {
    this.layoutObserver?.disconnect()
    this.layoutObserver = undefined
    this.observedLayout = undefined
    this.autoHidden = false
  }

  render() {
    return html`
      <nord-button variant="plain" ?square=${this.square} size=${this.size} ${ref(this.focusableRef)} class=${classMap({ 'is-rtl': this.direction.isRTL })}>
        <nord-visually-hidden>${this.localization.term('label')}</nord-visually-hidden>
        <nord-icon size=${this.iconSize} class="icon-default" name=${this.icon}></nord-icon>
        <nord-icon size=${this.iconSize} class="icon-active" name=${this.iconActive}></nord-icon>
      </nord-button>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-nav-toggle': NavToggle
  }
}
